// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { ChartOutput, GaugeWidgetType, WidgetOutput } from 'thehive-sdk';
import { WidgetError } from '@Organisms/Dashboard/DashboardDetails/WidgetError';
import { Gauge, GaugeConfig } from '@ant-design/charts/';
import { HTMLColors } from '@Constants/colors';
import { WidgetLoading } from '@Organisms/Dashboard/DashboardDetails/WidgetLoading/WidgetLoading';
import { UseQueryResult } from 'react-query';

interface GaugeWidgetProps {
    widget: WidgetOutput;
    query?: UseQueryResult<ChartOutput>;
}

export const GaugeWidget = ({ widget, query }: GaugeWidgetProps): ReactElement => {
    const options = widget.options as GaugeWidgetType;

    const { safe, warning, critical } = options;

    if (query?.isLoading) {
        return (
            <div className="widget__empty">
                <WidgetLoading />
            </div>
        );
    }

    if (query?.status === 'error') {
        return <WidgetError />;
    }

    if (!query?.data) {
        return <></>;
    }

    const gauge = query?.data['count'] || 0;

    const color = gauge > warning ? HTMLColors.RED : gauge > safe ? HTMLColors.AMBER : HTMLColors.GREEN;
    const gaugePercent = (gauge * 100) / critical / 100;
    const ticks = [0, (safe * 100) / critical / 100, (warning * 100) / critical / 100, 1];

    const config: GaugeConfig = {
        percent: gaugePercent,
        range: {
            ticks,
            color: [HTMLColors.GREEN, HTMLColors.AMBER, HTMLColors.RED],
        },
        indicator: {
            pointer: { style: { stroke: '#D0D0D0' } },
            pin: { style: { stroke: '#D0D0D0' } },
        },
        statistic: {
            content: {
                style: {
                    fontSize: '36px',
                    lineHeight: '36px',
                    color,
                },
                formatter: () => gauge + '',
            },
        },
        className: 'chart',
    };

    return <Gauge {...config} />;
};
